import { useCallback } from 'react';
import { useRecoilState } from 'recoil';
import { BFF_ENDPOINTS } from 'src/app/constants';
import networkService from 'src/app/lib/network-service';
import withFeedbackMapper from 'src/app/utils/withFeedbackMapper';
import {
  accessTokenAtom,
  conversationsAtom,
  feedbackAtom,
  frameParamsAtom,
  tutorStateAtom,
} from 'src/app/state/atoms';
import { ISendFeedbackPayloadType } from 'src/types/serviceResponseTypes';
import { MarkFeedbackType } from 'src/types/generic';

/**
 * Provisional hook to mark user Feedback
 */

function useFeedback(): [(options: MarkFeedbackType) => void] {
  const [accessToken] = useRecoilState(accessTokenAtom);
  const [conversations, setConversations] = useRecoilState(conversationsAtom);
  const [feedbackData, setFeedbackData] = useRecoilState(feedbackAtom);
  const [tutorState, setTutorState] = useRecoilState(tutorStateAtom);
  const [frameParams] = useRecoilState(frameParamsAtom);

  /**
   * In SME mode there is no conversation id available
   * so we pass exercise id into payload
   */
  const getConversationIDForSME = useCallback(
    () => (isNaN(parseInt(frameParams.exercise_id)) ? 0 : parseInt(frameParams.exercise_id)),
    [frameParams.exercise_id]
  );

  const prepareFeedbackPayload = useCallback(
    (options: MarkFeedbackType): ISendFeedbackPayloadType => ({
      conversationId: tutorState.isSMEMode ? getConversationIDForSME() : conversations.data.id,
      bookCode: frameParams.book_code ?? '',
      exerciseId: frameParams.exercise_id,
      messageId: options.messageId ?? 0,
      positive: options.type === 'positive',
      open: options.response ?? '',
      selected: [options.selection ?? ''] ?? [],
    }),
    [conversations.data.id, frameParams, tutorState.isSMEMode, getConversationIDForSME]
  );

  const markFeedback = useCallback(
    (options: MarkFeedbackType) => {
      networkService
        .post<ISendFeedbackPayloadType>(
          (process.env.REACT_APP_BASE_URL ?? '') + BFF_ENDPOINTS.MARK_FEEDBACK,
          JSON.stringify(prepareFeedbackPayload(options)),
          {
            'X-Authorization': 'Bearer ' + accessToken,
          }
        )
        .then((response) => {
          if (response.status) {
            // map the conversations with feedback value
            const newFeedback = [
              ...feedbackData,
              {
                messageId: options.messageId ?? -1,
                isPositiveMarked: options.type === 'positive',
                isNegativeMarked: options.type === 'negative',
              },
            ];

            setFeedbackData(newFeedback);

            if (options.modalSkip) {
              setTutorState((prev) => ({
                ...prev,
                ...(options.type === 'positive'
                  ? {
                      skipPositiveModal: true,
                    }
                  : {
                      skipNegativeModal: true,
                    }),
              }));
            }
            setConversations((prevConversations) => ({
              ...prevConversations,
              data: withFeedbackMapper(prevConversations.data, newFeedback),
            }));
          } else {
            setConversations((prevConversations) => ({
              ...prevConversations,
              error: response.error ?? '',
            }));
          }
        });
    },
    [
      accessToken,
      feedbackData,
      setTutorState,
      prepareFeedbackPayload,
      setFeedbackData,
      setConversations,
    ]
  );

  return [markFeedback];
}

export default useFeedback;
